import { z } from 'zod'
import { streamColumnLabel } from './stream-columns'

/** What an insight spec can say: the dimensions, measures, charts, filters and style it names.
 *  The module header in insights.ts explains the design. */

// ── Vocabulary ─────────────────────────────────────────────────────────────────────────────────

/** The fields a spec may group or filter by — the stream row's labels, plus fields derived from
 *  them: `syndication` (cluster role), `weekday` / `hour` (UTC, from the publish time) and
 *  `reachBand`. `topic` and `tag` are multi-valued: a hit counts once in each. `headline` is the
 *  story: copies that ran under the same headline (a wire story and its pickups) group together,
 *  which is what "the biggest stories" and "top headlines" mean. */
export const INSIGHT_DIMENSIONS = [
  'date',
  'mediaType',
  'outlet',
  'domain',
  'author',
  'folder',
  'topic',
  'tag',
  'sentiment',
  'classification',
  'placement',
  'syndication',
  'weekday',
  'hour',
  'reachBand',
  'headline',
] as const
export type BuiltinInsightDimension = (typeof INSIGHT_DIMENSIONS)[number]
/** A column the analytics build wrote beyond the fixed row (stream-columns.ts), by its key —
 *  `field:OutletTier`, `field:narrative`. Any column the rows carry can be grouped and filtered
 *  by; `critiqueInsight` refuses one they do not. */
export type InsightFieldRef = `field:${string}`
export const INSIGHT_FIELD_PREFIX = 'field:'
const insightFieldRefSchema = z.custom<InsightFieldRef>(
  (value) => typeof value === 'string' && /^field:.{1,80}$/.test(value),
  'A build column is "field:<name>"',
)
export const insightDimensionSchema = z.union([z.enum(INSIGHT_DIMENSIONS), insightFieldRefSchema])
export type InsightDimension = BuiltinInsightDimension | InsightFieldRef
/** Every dimension but the date — what a filter, a value lookup or a profile names. */
export const insightLabelFieldSchema = z.union([
  z.enum(INSIGHT_DIMENSIONS).exclude(['date']),
  insightFieldRefSchema,
])
export const isInsightFieldRef = (field: string): field is InsightFieldRef =>
  field.startsWith(INSIGHT_FIELD_PREFIX)
export const insightFieldKey = (field: InsightFieldRef) => field.slice(INSIGHT_FIELD_PREFIX.length)

export const INSIGHT_DIMENSION_LABELS: Record<BuiltinInsightDimension, string> = {
  date: 'Date',
  mediaType: 'Media type',
  outlet: 'Outlet',
  domain: 'Domain',
  author: 'Author',
  folder: 'Folder',
  topic: 'Topic',
  tag: 'Tag',
  sentiment: 'Sentiment',
  classification: 'Classification',
  placement: 'Placement',
  syndication: 'Syndication',
  weekday: 'Weekday',
  hour: 'Hour (UTC)',
  reachBand: 'Reach band',
  headline: 'Headline',
}

/** A dimension's name for people — a built-in's, or a build column's from its key. */
export function insightDimensionLabel(field: InsightDimension): string {
  return isInsightFieldRef(field)
    ? streamColumnLabel(insightFieldKey(field))
    : INSIGHT_DIMENSION_LABELS[field]
}

export const INSIGHT_MEASURES = [
  'count',
  'reach',
  'avgReach',
  'medianReach',
  'outlets',
  'authors',
  'netSentiment',
  'negativeShare',
  'positiveShare',
  'neutralShare',
  'pickupShare',
] as const
export type BuiltinInsightMeasure = (typeof INSIGHT_MEASURES)[number]
/** A figure column the analytics build wrote, added up or averaged — `sum:emv`,
 *  `avg:ThemeScores.AI`. Over the rows that carry a figure; null where none does. */
export type InsightFieldMeasure = `sum:${string}` | `avg:${string}`
const insightFieldMeasureSchema = z.custom<InsightFieldMeasure>(
  (value) => typeof value === 'string' && /^(sum|avg):.{1,80}$/.test(value),
  'A build figure is "sum:<name>" or "avg:<name>"',
)
export const insightMeasureSchema = z.union([z.enum(INSIGHT_MEASURES), insightFieldMeasureSchema])
export type InsightMeasure = BuiltinInsightMeasure | InsightFieldMeasure
export const isInsightFieldMeasure = (measure: string): measure is InsightFieldMeasure =>
  /^(sum|avg):/.test(measure)

export const INSIGHT_MEASURE_LABELS: Record<BuiltinInsightMeasure, string> = {
  count: 'Hits',
  reach: 'Total reach',
  avgReach: 'Average reach',
  medianReach: 'Median reach',
  outlets: 'Distinct outlets',
  authors: 'Distinct authors',
  netSentiment: 'Net sentiment',
  negativeShare: '% negative',
  positiveShare: '% positive',
  neutralShare: '% neutral',
  pickupShare: '% pickups',
}

/** A measure's name for people: "Total EMV", "Average theme scores · AI" for a build figure. */
export function insightMeasureLabel(measure: InsightMeasure): string {
  if (!isInsightFieldMeasure(measure)) return INSIGHT_MEASURE_LABELS[measure]
  const label = streamColumnLabel(measure.slice(4))
  return measure.startsWith('sum:') ? `Total ${label}` : `Average ${label}`
}

export const INSIGHT_CHARTS = [
  'kpi',
  'bar',
  'hbar',
  'stackedBar',
  'line',
  'area',
  'stackedArea',
  'combo',
  'donut',
  'treemap',
  'scatter',
  'bubble',
  'table',
  'heatmap',
] as const
export const insightChartSchema = z.enum(INSIGHT_CHARTS)
export type InsightChart = z.infer<typeof insightChartSchema>

export const INSIGHT_BUCKETS = ['day', 'week', 'month'] as const
export type InsightBucket = (typeof INSIGHT_BUCKETS)[number]

export const INSIGHT_TRANSFORMS = [
  'none',
  'cumulative',
  'rolling',
  'shareOfTotal',
  'change',
] as const
export const insightTransformSchema = z.enum(INSIGHT_TRANSFORMS)
export type InsightTransform = z.infer<typeof insightTransformSchema>

/** By label: one of the values (`in`) or none of them (`notIn`), case-insensitively. */
export const insightLabelFilterSchema = z.object({
  field: insightLabelFieldSchema,
  op: z.enum(['in', 'notIn']),
  values: z.array(z.string().min(1).max(200)).min(1).max(25),
})
/** By words in the article — headline or snippet contains any of them (or none, `notContains`). */
export const insightTextFilterSchema = z.object({
  field: z.literal('text'),
  op: z.enum(['contains', 'notContains']),
  values: z.array(z.string().trim().min(2).max(100)).min(1).max(10),
})
/** By an article's reach. */
export const insightReachFilterSchema = z.object({
  field: z.literal('reach'),
  op: z.enum(['gt', 'gte', 'lt', 'lte']),
  value: z.number().min(0),
})
export const insightFilterSchema = z.union([
  insightLabelFilterSchema,
  insightTextFilterSchema,
  insightReachFilterSchema,
])
export type InsightLabelFilter = z.infer<typeof insightLabelFilterSchema>
export type InsightFilter = z.infer<typeof insightFilterSchema>

/** A named group of rows — "Acme" = tag Acme; "Competitors" = tag X or tag Y. A row may fall in
 *  several segments, and counts in each. */
export const insightSegmentSchema = z.object({
  name: z.string().min(1).max(40),
  where: z.array(insightFilterSchema).min(1).max(8),
})
export type InsightSegment = z.infer<typeof insightSegmentSchema>

export const INSIGHT_LIMIT_MAX = 50
export const INSIGHT_SERIES_MAX = 8
export const INSIGHT_FACETS_MAX = 9

// ── Style ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The colours a chart may name. Each resolves to a brand-system token in the renderer (so it
 * follows light and dark mode); a `#rrggbb` the reader typed — a client's own brand red — is the
 * one literal accepted, because it is their content, not a colour baked into a component.
 */
export const INSIGHT_COLORS = [
  'blue',
  'green',
  'orange',
  'teal',
  'purple',
  'red',
  'amber',
  'gray',
  'navy',
] as const
export const insightColorSchema = z.union([
  z.enum(INSIGHT_COLORS),
  z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A colour is a name or #rrggbb'),
])
export type InsightColor = z.infer<typeof insightColorSchema>

export const INSIGHT_PALETTES = ['brand', 'cool', 'warm', 'mono', 'muted'] as const

/** How a chart looks. Every field has a default, so a spec without a style draws as it always did. */
export const insightStyleSchema = z.object({
  /** The series order of colours: brand hues, cool or warm ones, shades of the brand blue, or
   *  "muted" — grey, so only what `colors` names stands out. */
  palette: z.enum(INSIGHT_PALETTES).default('brand'),
  /** The colour of a single-series chart's marks. */
  color: insightColorSchema.nullable().default(null),
  /** A colour per label — a bar, a slice, a series, a segment. Matched case-insensitively. */
  colors: z
    .record(z.string().min(1).max(200), insightColorSchema)
    .refine((record) => Object.keys(record).length <= 12, 'At most 12 colours')
    .default({}),
  /** Print each value on its bar, point or slice. */
  showValues: z.boolean().default(false),
  legend: z.enum(['auto', 'hidden', 'bottom', 'right']).default('auto'),
  curve: z.enum(['smooth', 'straight', 'step']).default('smooth'),
  /** Dots on line points. */
  dots: z.boolean().default(false),
  grid: z.boolean().default(true),
  weight: z.enum(['thin', 'regular', 'bold']).default('regular'),
  /** Rounded bar ends; square when false. */
  rounded: z.boolean().default(true),
  /** A donut's hole; false draws a pie. */
  donutHole: z.boolean().default(true),
  /** Axis titles; null = none. */
  xTitle: z.string().max(60).nullable().default(null),
  yTitle: z.string().max(60).nullable().default(null),
  /** Tilt long category labels on a vertical-bar or time axis. */
  tiltLabels: z.boolean().default(false),
  /** The value axis's range; null = fitted. A minimum above zero zooms in on a change. */
  yMin: z.number().nullable().default(null),
  yMax: z.number().nullable().default(null),
  /** Logarithmic for values spanning orders of magnitude (reach). Zero and below are not drawn. */
  yScale: z.enum(['linear', 'log']).default('linear'),
  /** "compact" = 12.3K; "full" = 12,345. */
  numberFormat: z.enum(['auto', 'compact', 'full']).default('auto'),
  /** Decimal places; null = what suits the value. */
  decimals: z.number().int().min(0).max(3).nullable().default(null),
  /** Date labels: "Oct 3" (short), "3 October 2026" (long), or the raw yyyy-mm-dd (iso). */
  dateFormat: z.enum(['auto', 'short', 'long', 'iso']).default('auto'),
  /** A target to measure against, drawn as a line across the value axis. */
  goal: z
    .object({ value: z.number(), label: z.string().max(40).default('Goal') })
    .nullable()
    .default(null),
  /** Notes pinned to the category axis — "Launch day" on a date, "Peak" on an outlet. `at` is a
   *  category label or a yyyy-mm-dd on a time axis. */
  annotations: z
    .array(z.object({ at: z.string().min(1).max(60), label: z.string().min(1).max(40) }))
    .max(6)
    .default([]),
  /** Colour by value — "bars over 100 in red". The first rule a value meets wins. */
  colorRules: z
    .array(
      z.object({
        op: z.enum(['gt', 'gte', 'lt', 'lte']),
        value: z.number(),
        color: insightColorSchema,
      }),
    )
    .max(4)
    .default([]),
})
export type InsightStyle = z.infer<typeof insightStyleSchema>

/**
 * One chart, described. `x` is the axis (null only for a single figure, or when `segments` are the
 * categories); `series` splits each bar/line/cell by a second dimension, and `segments`, when
 * given, are the split instead; `where` narrows by labels (AND across filters, OR within one's
 * values, matched case-insensitively). `limit` caps the categories of a non-date axis, biggest
 * first unless `sort` says otherwise — null means the natural order: time for `date`, the
 * dimension's own order for weekday/hour/reach band, biggest first for everything else.
 */
export const insightSpecSchema = z.object({
  title: z.string().min(1).max(80),
  /** One sentence under the title — the takeaway, not a restatement. */
  subtitle: z.string().max(160).nullable().default(null),
  chart: insightChartSchema,
  x: insightDimensionSchema.nullable().default(null),
  series: insightDimensionSchema.nullable().default(null),
  segments: z.array(insightSegmentSchema).max(INSIGHT_SERIES_MAX).default([]),
  measure: insightMeasureSchema.default('count'),
  /** Applied to the measure along the axis, per series. `rolling` averages `window` buckets. */
  transform: insightTransformSchema.default('none'),
  window: z.number().int().min(2).max(30).default(7),
  /** A second measure over each category, drawn as a line on its own axis. */
  secondary: z
    .object({
      measure: insightMeasureSchema,
      transform: insightTransformSchema.default('none'),
    })
    .nullable()
    .default(null),
  /** scatter / bubble: each category of `x` is a point placed by two measures, sized by a third. */
  points: z
    .object({
      x: insightMeasureSchema,
      y: insightMeasureSchema,
      size: insightMeasureSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
  /** Small multiples: one panel per value of this dimension, on shared axes. */
  facet: insightDimensionSchema.nullable().default(null),
  facetLimit: z.number().int().min(2).max(INSIGHT_FACETS_MAX).default(6),
  bucket: z.enum(INSIGHT_BUCKETS).default('day'),
  where: z.array(insightFilterSchema).max(8).default([]),
  /** Only the newest N days of the rows in scope; null = all of them. */
  lastDays: z.number().int().min(1).max(365).nullable().default(null),
  /** An exact range, yyyy-mm-dd, both ends inclusive — instead of, or narrowing, `lastDays`. */
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  /** This period against the one of equal length before it — for a figure, or a category chart
   *  (two series: "This period", "Previous period"). Needs `lastDays` or a date range. */
  compare: z.enum(['none', 'previous']).default('none'),
  limit: z.number().int().min(1).max(INSIGHT_LIMIT_MAX).default(10),
  sort: z.enum(['valueDesc', 'valueAsc', 'label']).nullable().default(null),
  /** Stacked charts: each x bucket as 100%. Ignored for a non-additive measure. */
  percent: z.boolean().default(false),
  /** A horizontal guide at the average or median of the plotted values. */
  reference: z.enum(['none', 'average', 'median']).default('none'),
  /** Labels to draw in full colour while the rest recede — the story's subjects. */
  highlight: z.array(z.string().min(1).max(200)).max(10).default([]),
  /** Display names — "(none)" → "Untagged", "Rival" → "Competitor A". Keys are the labels as the
   *  data has them (matched case-insensitively); only what is SHOWN changes, so click-throughs and
   *  filters keep working. */
  labels: z
    .record(z.string().min(1).max(200), z.string().min(1).max(60))
    .refine((record) => Object.keys(record).length <= 40, 'At most 40 renamed labels')
    .default({}),
  /** A custom category order, first to last; anything unlisted follows in its usual order. */
  order: z.array(z.string().min(1).max(200)).max(50).default([]),
  /** How it looks — colours, labels, legend, line and bar style. */
  style: insightStyleSchema.prefault({}),
})
export type InsightSpec = z.infer<typeof insightSpecSchema>
